import { useCallback, useEffect, useState } from "react";
import { Plus, Copy, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import { PermissionEditor } from "../components/PermissionEditor";
import { presetPerms, matchesPreset, ALL_PERM_KEYS } from "../lib/permissions";
import api, { apiError, logError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function DiscountField({ value, onChange, testId }) {
  return (
    <div className="px-2.5 py-2.5 border-t border-[#F0ECE3]">
      <p className="text-sm">Remise maximale autorisée</p>
      <p className="text-[11px] text-gray-500 mb-1.5">Sur le total de la vente. 0 % = aucune remise.</p>
      <div className="flex items-center gap-2">
        <input data-testid={testId} type="number" step="0.1" min="0" max="100"
          className="kinput max-w-[110px]" value={value} onChange={(e) => onChange(e.target.value)} />
        <span className="text-sm text-gray-500">%</span>
      </div>
    </div>
  );
}

function TeamStatus({ u, onCopy }) {
  if (u.pending) {
    return (
      <>
        <span className="badge-partial px-2.5 py-1 rounded-full text-xs font-semibold">Invitation en attente</span>
        <button data-testid="copy-invite-btn" onClick={() => onCopy(`${window.location.origin}/invitation/${u.invite_token}`)}
          className="btn-ghost py-1.5 px-3 text-xs flex items-center gap-1"><Copy size={13} /> Copier le lien</button>
      </>
    );
  }
  if (u.active) return <span className="badge-paid px-2.5 py-1 rounded-full text-xs font-semibold">Actif</span>;
  return <span className="badge-unpaid px-2.5 py-1 rounded-full text-xs font-semibold">Accès révoqué</span>;
}

function UserRow({ u, onPatch, onReinvite, onCopy, customAllowed }) {
  const [open, setOpen] = useState(false);
  const [perms, setPerms] = useState(() => Object.fromEntries(ALL_PERM_KEYS.map((k) => [k, !!u.permissions?.[k]])));
  const [cap, setCap] = useState(String(u.max_discount_percent ?? 0));

  const save = () => onPatch(u.id, { permissions: perms, max_discount_percent: parseFloat(cap) || 0 });

  return (
    <div data-testid="team-user-row" className="kcard p-4 flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{u.name}</p>
        <p className="text-xs text-gray-500">{u.email || u.phone} · <span className="capitalize">{u.role}</span>
          {u.role !== "patron" && <span data-testid={`max-discount-label-${u.id}`}> · remise max. {u.max_discount_percent ?? 0} %</span>}
        </p>
      </div>
      <TeamStatus u={u} onCopy={onCopy} />
      {u.role !== "patron" && (
        <>
          {!u.pending && (
            <button data-testid={`edit-perms-btn-${u.id}`} onClick={() => setOpen((o) => !o)}
              className="text-xs text-[#C9793A] font-semibold">{open ? "Masquer les accès" : "Modifier les accès"}</button>
          )}
          {u.active && (
            <button data-testid="deactivate-user-btn" onClick={() => onPatch(u.id, { active: false })}
              className="text-xs text-[#B4432F] font-semibold">Révoquer l'accès</button>
          )}
          {!u.active && (
            <button data-testid="reinvite-user-btn" onClick={() => onReinvite(u)}
              className="text-xs text-[#2E7D5B] font-semibold flex items-center gap-1"><ArrowClockwise size={13} /> Renvoyer une invitation</button>
          )}
        </>
      )}
      {open && (
        <div className="w-full mt-3 pt-3 border-t border-[#E9E4DA]">
          {customAllowed ? (
            <>
              <PermissionEditor perms={perms} setPerms={setPerms} idSuffix={`-${u.id}`}
                discountField={<DiscountField value={cap} onChange={setCap} testId={`max-discount-input-${u.id}`} />} />
              <button data-testid={`save-perms-btn-${u.id}`} className="btn-amber w-full py-2.5 mt-3" onClick={save}>
                Enregistrer les accès
              </button>
            </>
          ) : (
            <p className="text-xs text-[#9a7620]">
              Les droits détaillés sont disponibles à partir du Palier 3 (PME structurée). Votre palier utilise les
              rôles prédéfinis Vendeur et Gestionnaire.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Team() {
  const { has } = useAuth();
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [role, setRole] = useState("vendeur");
  const [perms, setPerms] = useState(() => presetPerms("vendeur"));
  const [cap, setCap] = useState("0");
  const [inviteLink, setInviteLink] = useState(null);
  const customAllowed = has("custom_permissions");

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch((e) => logError(e)), []);
  useEffect(() => { load(); }, [load]);

  const copyLink = (link) => {
    navigator.clipboard.writeText(link)
      .then(() => toast.success("Lien copié — partagez-le par WhatsApp ou SMS"))
      .catch(() => toast.error(`Copie impossible sur ce navigateur. Copiez le lien à la main : ${link}`));
  };

  const openInvite = () => {
    setShowInvite(true); setInviteLink(null);
    setForm({ first_name: "", last_name: "", phone: "", email: "" });
    setRole("vendeur"); setPerms(presetPerms("vendeur")); setCap("0");
  };

  const invite = async () => {
    try {
      const body = {
        name: `${form.first_name} ${form.last_name}`.trim(),
        first_name: form.first_name, last_name: form.last_name,
        email: form.email.trim(), phone: form.phone.trim(),
        contact: form.email.trim() || form.phone.trim(),
        role: matchesPreset(perms, "gestionnaire") ? "gestionnaire" : "vendeur",
        max_discount_percent: parseFloat(cap) || 0,
      };
      if (customAllowed) body.permissions = perms;
      const { data } = await api.post("/users/invite", body);
      setInviteLink(`${window.location.origin}${data.invite_path}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const patchUser = async (id, updates) => {
    try {
      await api.patch(`/users/${id}`, updates);
      toast.success("Accès mis à jour — effet immédiat, tracé dans le journal");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const reinvite = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reinvite`);
      copyLink(`${window.location.origin}${data.invite_path}`);
      toast.success(`Nouvelle invitation créée pour ${u.name} — lien copié`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="team-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl">Équipe & accès</h1>
          <p className="text-xs text-gray-500">Chaque employé définit lui-même son mot de passe. Vous ne pouvez jamais le connaître ni le réinitialiser — seulement révoquer l'accès puis renvoyer une invitation.</p>
        </div>
        <button data-testid="invite-employee-btn" onClick={openInvite} className="btn-amber py-2 px-4 flex items-center gap-1.5 text-sm"><Plus size={16} weight="bold" /> Ajouter un utilisateur</button>
      </div>

      <div className="space-y-2">
        {users.map((u) => (
          <UserRow key={u.id} u={u} onPatch={patchUser} onReinvite={reinvite} onCopy={copyLink} customAllowed={customAllowed} />
        ))}
      </div>

      {showInvite && (
        <ModalShell size="max-w-lg" onClose={() => setShowInvite(false)}>
          <h3 className="font-display font-bold text-lg">Ajouter un utilisateur</h3>
          {inviteLink ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Invitation créée. Partagez ce lien à l'employé (WhatsApp, SMS, email) — il choisira lui-même son mot de passe :</p>
              <p data-testid="invite-link-display" className="font-mono-doc text-xs bg-[#F6F4EF] rounded-xl p-3 break-all">{inviteLink}</p>
              <div className="flex gap-2">
                <button data-testid="invite-copy-btn" className="btn-amber flex-1 py-2.5 flex items-center justify-center gap-1.5" onClick={() => copyLink(inviteLink)}><Copy size={15} /> Copier le lien</button>
                <button className="btn-ghost flex-1 py-2.5" onClick={() => setShowInvite(false)}>Fermer</button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="invite-firstname-input" className="kinput" placeholder="Prénom *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                <input data-testid="invite-lastname-input" className="kinput" placeholder="Nom *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <input data-testid="invite-phone-input" className="kinput" inputMode="tel" placeholder="Téléphone * (pour l'invitation)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input data-testid="invite-email-input" className="kinput" placeholder="Email (facultatif)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

              <PermissionEditor perms={perms} setPerms={setPerms} role={role} setRole={setRole}
                disabled={!customAllowed && role === "custom"} idSuffix="-new"
                discountField={<DiscountField value={cap} onChange={setCap} testId="invite-max-discount-input" />} />
              {!customAllowed && (
                <p className="text-xs text-[#9a7620]">
                  Votre palier utilise les rôles prédéfinis : les ajustements interrupteur par interrupteur sont
                  disponibles à partir du Palier 3 (PME structurée).
                </p>
              )}

              <div className="flex gap-2">
                <button className="btn-ghost flex-1 py-2.5" onClick={() => setShowInvite(false)}>Annuler</button>
                <button data-testid="invite-submit-btn" className="btn-amber flex-1 py-2.5"
                  disabled={!form.first_name || !form.last_name || !(form.phone || form.email)} onClick={invite}>
                  Envoyer l'invitation
                </button>
              </div>
              <p className="text-[11px] text-gray-500">L'utilisateur reçoit un lien d'invitation et choisit lui-même son mot de passe.</p>
            </>
          )}
        </ModalShell>
      )}
    </div>
  );
}
